import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineSpeakerWave } from 'react-icons/hi2';
import { RiVoiceprintLine } from 'react-icons/ri';
import { MdOutlineCheck } from 'react-icons/md';
import { HiOutlineX } from 'react-icons/hi';

const VOICE_STORAGE_KEY = 'kds-tts-voice';
const DEMO_TEXT = 'Order received. 2 orders of Chicken Momo. 1 order of French Fries.';

const VoiceSettings = ({ collapsed = false }) => {
    const [open, setOpen] = useState(false);
    const [voices, setVoices] = useState([]);
    const [selected, setSelected] = useState('');
    const [pending, setPending] = useState('');
    const [playing, setPlaying] = useState('');
    const [panelStyle, setPanelStyle] = useState({});

    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    // ── Load voices ────────────────────────────────────────────────────────────
    useEffect(() => {
        const sync = () => {
            const all = window.speechSynthesis.getVoices().filter(v => v.lang.startsWith('en'));
            setVoices(all);
        };
        sync();
        window.speechSynthesis.addEventListener('voiceschanged', sync);
        return () => window.speechSynthesis.removeEventListener('voiceschanged', sync);
    }, []);

    // ── Sync saved voice when panel opens ─────────────────────────────────────
    useEffect(() => {
        if (open) {
            const saved = localStorage.getItem(VOICE_STORAGE_KEY) || '';
            setSelected(saved);
            setPending(saved);
        }
    }, [open]);

    // ── Position panel relative to trigger button ──────────────────────────────
    useEffect(() => {
        if (!open || !triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const PANEL_W = 380;
        const GAP = 8;

        // Horizontal: open just to the right of the trigger, clamped to viewport
        const left = Math.max(8, Math.min(rect.right + GAP, window.innerWidth - PANEL_W - 8));

        // Vertical: center in the viewport (top-to-bottom), regardless of trigger
        // position or actual panel height — CSS handles centering for us, so it
        // stays exact even as the voice list grows/shrinks.
        setPanelStyle({
            left,
            width: PANEL_W,
            top: '50%',
            transform: 'translateY(-50%)',
            maxHeight: 'calc(100vh - 16px)', // safety net on very short viewports
        });
    }, [open, collapsed]);

    // ── Close on outside click ─────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            const clickedTrigger = triggerRef.current?.contains(e.target);
            const clickedPanel = panelRef.current?.contains(e.target);
            if (!clickedTrigger && !clickedPanel) {
                setOpen(false);
                window.speechSynthesis.cancel();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // ── Demo ──────────────────────────────────────────────────────────────────
    const playDemo = (voice) => {
        window.speechSynthesis.cancel();
        setPlaying(voice.name);
        const u = new SpeechSynthesisUtterance(DEMO_TEXT);
        u.voice = voice;
        u.rate = 0.88;
        u.pitch = 1.0;
        u.volume = 1;
        u.onend = () => setPlaying('');
        u.onerror = () => setPlaying('');
        window.speechSynthesis.speak(u);
    };

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = () => {
        localStorage.setItem(VOICE_STORAGE_KEY, pending);
        setSelected(pending);
        setOpen(false);
        window.speechSynthesis.cancel();

        // Notify any component using the saved voice (e.g. Card.jsx) to refresh immediately.
        // We use a custom event instead of relying on the native 'storage' event because
        // 'storage' only fires in OTHER tabs/windows, never in the same tab that wrote it.
        window.dispatchEvent(new CustomEvent('kds-voice-changed', { detail: pending }));
    };

    // ── Trigger button ────────────────────────────────────────────────────────
    const triggerBtn = collapsed ? (
        <button
            ref={triggerRef}
            onClick={() => setOpen(v => !v)}
            title="Voice settings"
            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors
                ${open ? 'bg-violet-50 text-violet-600' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-800'}`}
        >
            <RiVoiceprintLine size={18} />
        </button>
    ) : (
        <button
            ref={triggerRef}
            onClick={() => setOpen(v => !v)}
            className={`flex items-center gap-2.5 px-3 py-2 w-full rounded-lg text-sm font-medium transition-colors
                ${open ? 'bg-violet-50 text-violet-600' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}
        >
            <RiVoiceprintLine size={17} className="shrink-0" />
            <span>Voice settings</span>
        </button>
    );

    // ── Panel (portalled to body) ──────────────────────────────────────────────
    const panel = open && createPortal(
        <div
            ref={panelRef}
            style={{ position: 'fixed', zIndex: 9999, ...panelStyle }}
            className="bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden flex flex-col ml-4"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 shrink-0">
                <div className="text-sm font-semibold text-gray-800">Voice settings</div>
                <button
                    onClick={() => { setOpen(false); window.speechSynthesis.cancel(); }}
                    className="p-1.5 flex items-center justify-center rounded-md hover:bg-gray-100 text-gray-400 hover:!text-gray-700"
                >
                    <HiOutlineX size={16} />
                </button>
            </div>

            {/* Demo text */}
            <div className="px-4 py-2.5 shrink-0">
                <div className="text-xs text-gray-400 font-medium">
                    Demo: <span className="italic text-gray-500">&ldquo;{DEMO_TEXT}&rdquo;</span>
                </div>
            </div>

            {/* Voice list */}
            <ul className="overflow-y-auto px-2 py-1.5 flex flex-col gap-0.5 tiny-scrollbar" style={{ maxHeight: 300 }}>
                {voices.length === 0 && (
                    <li className="px-3 py-4 text-xs text-center text-gray-400">
                        No English voices found on this device.
                    </li>
                )}
                {voices.map(voice => {
                    const isSelected = pending === voice.name;
                    const isPlaying = playing === voice.name;
                    return (
                        <li key={voice.name}>
                            <div
                                onClick={() => setPending(voice.name)}
                                className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors
                                    ${isSelected
                                        ? 'bg-violet-50 border border-violet-200'
                                        : 'hover:bg-gray-50 border border-transparent'
                                    }`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors
                                        ${isSelected ? 'bg-violet-600 border-violet-600' : 'border-gray-300'}`}
                                    >
                                        {isSelected && <MdOutlineCheck size={10} className="text-white" />}
                                    </div>
                                    <div className="min-w-0">
                                        <div
                                            className={`text-xs font-medium cursor-pointer ${isSelected ? "whitespace-normal text-violet-700" : "truncate text-gray-700"}`}
                                            title={voice.name}
                                        >
                                            {voice.name}
                                        </div>

                                        <div
                                            className="text-[10px] text-gray-500 truncate"
                                            title={`${voice.lang}${voice.localService ? ' · local' : ' · network'}`}
                                        >
                                            {voice.lang}
                                            {voice.localService ? ' · local' : ' · network'}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); playDemo(voice); }}
                                    title="Preview this voice"
                                    className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-md transition-colors
                                        ${isPlaying
                                            ? 'bg-violet-100 text-violet-600 animate-pulse'
                                            : 'hover:bg-gray-100 text-gray-400 hover:text-gray-600'
                                        }`}
                                >
                                    <HiOutlineSpeakerWave size={18} />
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* Save */}
            <div className="px-3 py-3 border-t border-gray-100 shrink-0">
                <button
                    onClick={handleSave}
                    disabled={pending === selected}
                    className="w-full py-2 rounded-lg text-xs font-semibold transition-all
                        bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98]
                        disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-violet-600"
                >
                    {pending === selected ? 'Saved' : 'Save voice'}
                </button>
            </div>
        </div>,
        document.body
    );

    return (
        <>
            {triggerBtn}
            {panel}
        </>
    );
};

export default VoiceSettings;